/**
 * Slider - Composant générique pour les contrôles DAW
 * Version simple et fonctionnelle extraite du MasterVolumeSlider
 */

import { createEffect, createSignal, type Component, type JSX } from 'solid-js';
import { cn } from '~/lib/utils';

export interface SliderProps {
  value?: number;
  onValueChange?: (value: number) => void;
  disabled?: boolean;
  orientation?: 'vertical' | 'horizontal';
  size?: 'sm' | 'md' | 'lg';
  thumbSize?: 'sm' | 'md' | 'lg';
  colors?: {
    track?: string;
    range?: string;
    thumb?: string;
    thumbBorder?: string;
  };
  class?: string;
  style?: JSX.CSSProperties;
}

const sizeClasses = {
  horizontal: {
    sm: 'h-1 w-full min-w-24',
    md: 'h-2 w-full min-w-24',
    lg: 'h-3 w-full min-w-24',
  },
  vertical: {
    sm: 'w-1 h-full min-h-32',
    md: 'w-2 h-full min-h-32',
    lg: 'w-3 h-full min-h-32',
  },
};

const thumbSizeClasses = {
  sm: 'size-3 border-1',
  md: 'size-4 border-1',
  lg: 'size-6 border-2',
};

const defaultColors = {
  track: 'bg-muted',
  range: 'bg-primary',
  thumb: 'bg-surface-foreground',
  thumbBorder: 'border-accent',
};

export const Slider: Component<SliderProps> = (props) => {
  const [value, setValue] = createSignal(props.value ?? 0);
  const [isDragging, setIsDragging] = createSignal(false);
  createEffect(() => setValue(props.value ?? 0));

  const colors = { ...defaultColors, ...props.colors };
  const orientation = () => props.orientation ?? 'horizontal';
  const sizeClass = () => sizeClasses[orientation()][props.size || 'md'];
  const thumbSizeClass = thumbSizeClasses[props.thumbSize || 'md'];
  const isVertical = () => orientation() === 'vertical';

  const handleMouseDown = (e: MouseEvent) => {
    e.preventDefault();
    if (props.disabled) return;

    setIsDragging(true);

    const container = e.currentTarget as HTMLElement;
    updateValue(e, container);

    const handleMouseMove = (e: MouseEvent) => updateValue(e, container);

    const handleMouseUp = () => {
      setIsDragging(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const updateValue = (e: MouseEvent, container: HTMLElement) => {
    const rect = container.getBoundingClientRect();

    let percentage: number;

    if (isVertical()) {
      const y = e.clientY - rect.top;
      percentage = Math.max(0, Math.min(100, 100 - (y / rect.height) * 100));
    } else {
      const x = e.clientX - rect.left;
      percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    }

    setValue(Math.round(percentage));
    props.onValueChange?.(Math.round(percentage));
  };

  const getRangeStyle = () => {
    if (isVertical()) {
      return { height: `${value()}%` };
    } else {
      return { width: `${value()}%` };
    }
  };

  const getThumbStyle = () => {
    if (isVertical()) {
      return {
        top: `${100 - value()}%`,
        left: '50%',
      };
    } else {
      return {
        left: `${value()}%`,
        top: '50%',
      };
    }
  };

  return (
    <div
      data-slot="slider"
      class={cn(
        'slider-container relative cursor-pointer',
        {
          'opacity-50 cursor-not-allowed': props.disabled,
        },
        sizeClass(),
        props.class
      )}
      style={props.style}
      onMouseDown={handleMouseDown}
    >
      {/* Track */}
      <div data-slot="slider-track" class={cn('absolute inset-0 rounded-full', colors.track)} />

      {/* Range */}
      <div
        class={cn('absolute inset-0 rounded-full overflow-hidden flex pointer-events-none', {
          'flex-col': isVertical(),
          'flex-row-reverse': !isVertical(),
        })}
      >
        <div
          class={cn('bg-transparent', {
            'aspect-[1/2] h-full': !isVertical(),
            'aspect-[2/1] w-full': isVertical(),
          })}
        />
        <div class="flex-grow relative">
          <div
            data-slot="slider-range"
            class={cn('absolute', colors.range, {
              'bottom-0 w-full': isVertical(),
              'left-0 top-0 h-full': !isVertical(),
            })}
            style={getRangeStyle()}
          />
        </div>
        <div
          class={cn(colors.range, {
            'aspect-[1/2] h-full': !isVertical(),
            'aspect-[2/1] w-full': isVertical(),
          })}
        />
      </div>

      {/* Thumb */}
      <div
        data-slot="slider-thumb"
        class={cn('absolute inset-0 flex pointer-events-none', {
          'flex-col': isVertical(),
        })}
      >
        <div
          class={cn('bg-transparent', {
            'aspect-[1/2] h-full': !isVertical(),
            'aspect-[2/1] w-full': isVertical(),
          })}
        />
        <div class="flex-grow relative">
          <div
            class={cn(
              'absolute rounded-full shadow-lg transform -translate-x-1/2 -translate-y-1/2 z-10',
              thumbSizeClass,
              colors.thumb,
              colors.thumbBorder,
              {
                'scale-110': isDragging(),
              }
            )}
            style={getThumbStyle()}
          />
        </div>
        <div
          class={cn('bg-transparent', {
            'aspect-[1/2] h-full': !isVertical(),
            'aspect-[2/1] w-full': isVertical(),
          })}
        />
      </div>
    </div>
  );
};
